/**
 * SG Incorporation (Pre) — 문서 필드 채우기 엔진.
 * Panel 이 provideCddFill() 로 제공하고, CddVarField / DocScope 가 inject.
 */
import { reactive, ref, computed, provide, inject, type InjectionKey } from 'vue';
import {
  getB04Options,
  loadCmBaseCodeOptions,
  nationalityLabelForOutput,
} from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { resolveForm45UsesPassport } from '@/features/startTask/preset_task/sg/components/appoint-director/composables/form45Conditionals';

export type CddFieldType = 'text' | 'date' | 'entity' | 'person' | 'address' | 'currency' | 'sharetype' | 'nationality' | 'yesno' | 'ssic' | 'pepcategory';

/**
 * PEP 유형 — Client Acceptance form 원본이 체크박스로 고르게 돼 있는 5가지.
 * value 는 문서 렌더에서 어느 칸에 ☒ 를 찍을지 판단하는 데 쓰므로 바꾸지 말 것
 * (normalizeSgPreFormDataForWord 의 pep_cat_* 계산과 짝).
 */
export const PEP_CATEGORY_OPTIONS: Array<{ value: string; title: string }> = [
  { value: 'singapore_pep', title: 'Singapore PEP' },
  { value: 'foreign_pep', title: 'Foreign PEP' },
  { value: 'io_pep', title: 'International Organisation PEP' },
  { value: 'family', title: 'Family member of PEP' },
  { value: 'close_associate', title: 'Close associate of PEP' },
];

const OVERRIDES: Record<string, string> = {
  company_name: 'Company Name',
  company_registration_no: 'Company Registration No.',
  director_name: 'Director Name',
  secretary_name: 'Secretary Name',
  entity_name: 'Entity Name',
  has_pre_1993_history: 'Pre-1993 conviction history (iv)',
  is_employment_pass_holder: 'Employment Pass holder (bb)',
  needs_interpreter: 'Statements interpreted (c-ii)',
  uses_passport: 'Passport ID (not NRIC/FIN)',
  interpreter_language: 'Interpreter language / dialect',
  interpreter_name: 'Interpreter name',
  interpreter_nric: 'Interpreter NRIC / FIN',
};

function stripPrefix(key: string): string {
  return key.replace(/^(ind|lp|la|pt)_/, '');
}

/** DocScope `f45_1/` 등 prefix 제거 */
export function bareKey(k: string): string {
  return k.includes('/') ? k.slice(k.indexOf('/') + 1) : k;
}
export function scopeOf(k: string): string {
  return k.includes('/') ? k.slice(0, k.indexOf('/') + 1) : '';
}

export function labelize(key: string): string {
  const bare = stripPrefix(bareKey(key)).replace(/^(sec|dir|sh|ubo|pep|md|ceo|pa|ot)\d+_/, '');
  if (OVERRIDES[bare]) return OVERRIDES[bare];
  return bare
    .split('_')
    .map((w) => (w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

export function typeOf(key: string): CddFieldType {
  const b = stripPrefix(bareKey(key)).replace(/^(sec|dir|sh|ubo|pep|md|ceo|pa|ot)\d+_/, '');
  // 쪼개진 실행일(day/month/year) — 달력 없이 텍스트
  if (/^execution_(day|month|year)$/.test(b)) {
    return 'text';
  }
  // Proposed FYE / Financial Year End 등
  if (
    b === 'entity_proposed_fye' ||
    /_fye$/.test(b) ||
    /financial_year_end$/.test(b) ||
    /proposed_fye$/.test(b)
  ) {
    return 'date';
  }
  // Aura 문서의 작성일 'dated_on' / 'fbod_dated_on' 은 아래 date 패턴에 안 걸린다.
  //   Share Application · First BOD · Incorporation Documents 공통 서명일 → 달력 사용.
  //   first_accounts_period = First BOD 의 회계연도 종료일(달력).
  if (
    b === 'dated_on' ||
    /dated_on$/.test(b) ||
    b === 'first_accounts_period' ||
    /(^|_)(dob|date)($|_)/.test(b) ||
    /_date$/.test(b)
  ) {
    return 'date';
  }
  if (
    b === 'currency_symbol' ||
    b === 'currency' ||
    /^currency_/.test(b) ||
    /_currency$/.test(b)
  ) {
    return 'currency';
  }
  // PEP 유형 — 원본 문서가 5개 중 하나를 체크하는 서식이라 목록에서 고르게 한다
  if (/pep_category$/.test(b)) {
    return 'pepcategory';
  }
  // 국적 — CM 공통코드 B04 선택(직접 입력도 가능한 combobox)
  if (/nationality$/.test(b)) {
    return 'nationality';
  }
  // Class / type of shares (A20 Ordinary · Preference · Others)
  if (
    b === 'type_of_shares' ||
    b === 'security_type' ||
    b === 'share_class' ||
    b === 'class_of_shares' ||
    /type_of_shares$/.test(b) ||
    /class_of_shares?$/.test(b)
  ) {
    return 'sharetype';
  }
  // fee_*_address 는 금액 필드 (예: fee_annual_registered_address)
  if (/^fee_/.test(b)) {
    return 'text';
  }
  if (
    /(^|_)is_pep$/.test(b) ||
    /_is_public(_listed)?$/.test(b) ||
    b === 'cs_is_public_listed' ||
    b === 'ihc_is_public' ||
    b === 'upc_is_public' ||
    b === 'has_pre_1993_history' ||
    b === 'is_employment_pass_holder' ||
    b === 'needs_interpreter' ||
    b === 'uses_passport' ||
    // Aura Incorporation Documents 번들 — 체크박스(선택)
    /^svc_/.test(b) ||
    /^apply_/.test(b) ||
    /^(roc|rond)_at_/.test(b) ||
    /^mail_(collection|forward|scan)$/.test(b) ||
    b === 'audit_appoint' ||
    b === 'audit_arrange' ||
    b === 'bookkeeping_arrange'
  ) {
    return 'yesno';
  }
  if (/_address$/.test(b) || /address$/.test(b) || b === 'reply_to_address') {
    return 'address';
  }
  // SSIC / Nature of Business (Industry 텍스트 필드와 구분)
  if (
    b === 'entity_business_activity' ||
    b === 'cs_business_activity' ||
    /business_activity$/.test(b) ||
    /(^|_)ssic$/.test(b)
  ) {
    return 'ssic';
  }
  // CSP name = logged-in client (autofill only; no ✨ lookup)
  if (b === 'csp_name') {
    return 'text';
  }
  if (
    b === 'company_name' ||
    b === 'entity_name' ||
    b === 'proposed_company_name' ||
    b === 'cs_company_name' ||
    b === 'corporate_shareholder_name' ||
    b === 'controller_corporate_name' ||
    /controller_corporate_name$/.test(b) ||
    b === 'ihc_name' ||
    b === 'upc_name' ||
    b === 'agent_company_name' ||
    // Aura DDF — "Name of Corporation / Firm" 은 법인이므로 인물이 아니라 엔티티 조회
    b === 'corporation_name'
  ) {
    return 'entity';
  }
  // 이름처럼 보이지만 인물 lookup 대상이 아닌 라벨성 필드
  if (
    /former_name$|register_name$|legal_form$|_signature$/.test(b) ||
    b === 'bank_name' ||
    b === 'bank_branch' ||
    /_country$/.test(b) ||
    b === 'country' ||
    /country_of_incorporation$/.test(b)
  ) {
    return 'text';
  }
  if (
    /_contact_person$/.test(b) ||
    // Aura DDF — "Name of Registered Holder" (주식 명의자) 는 인물 조회 대상
    b === 'registered_holder' ||
    /_name$/.test(b) ||
    /full_name|director_name|secretary_name|shareholder_name|ubo_name|signer_name|declarant_name/.test(
      b,
    )
  ) {
    return 'person';
  }
  return 'text';
}

/** Contact / Officer 상세 → flat 주소 문자열 (SG: L_GLOBAL_CONTACT 컬럼 우선) */
function pickContactStr(obj: Record<string, any> | null | undefined, ...keys: string[]): string {
  if (!obj) return '';
  for (const k of keys) {
    const v = obj[k];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return '';
}

function addressPartsBag(
  person: Record<string, any>,
  kind: 'residential' | 'alternate',
): Record<string, any> {
  const raw =
    kind === 'alternate'
      ? person.alternateAddressParts || person.altAddressParts
      : person.primaryAddressParts || person.addressParts;
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, any>) : {};
}

/**
 * L_GLOBAL_CONTACT 주소 컬럼 → 한 줄 문자열.
 * MY registeredAddress / myResidential JSON 파싱과 무관.
 */
export function formatSgGlobalContactAddress(
  person: Record<string, any> | null | undefined,
  kind: 'residential' | 'alternate' = 'residential',
): string {
  if (!person) return '';
  const alt = kind === 'alternate';
  const parts = addressPartsBag(person, kind);
  const foreign1 = pickContactStr(
    person,
    alt ? 'ALT_FOREIGN_ADDRESS_LINE_1' : 'FOREIGN_ADDRESS_LINE_1',
    alt ? 'altForeignAddressLine1' : 'foreignAddressLine1',
    alt ? 'alternateForeignAddressLine1' : 'primaryForeignAddressLine1',
  ) || pickContactStr(parts, 'foreignAddressLine1', 'FOREIGN_ADDRESS_LINE_1');
  const foreign2 = pickContactStr(
    person,
    alt ? 'ALT_FOREIGN_ADDRESS_LINE_2' : 'FOREIGN_ADDRESS_LINE_2',
    alt ? 'altForeignAddressLine2' : 'foreignAddressLine2',
    alt ? 'alternateForeignAddressLine2' : 'primaryForeignAddressLine2',
  ) || pickContactStr(parts, 'foreignAddressLine2', 'FOREIGN_ADDRESS_LINE_2');
  if (foreign1 || foreign2) {
    return [foreign1, foreign2].filter(Boolean).join(', ');
  }

  const blockNo =
    pickContactStr(
      person,
      alt ? 'ALT_BLOCK_NO' : 'BLOCK_NO',
      alt ? 'altBlockNo' : 'blockNo',
      alt ? 'alternateBlockNo' : 'primaryBlockNo',
    ) || pickContactStr(parts, 'blockNo', 'BLOCK_NO');
  const streetName =
    pickContactStr(
      person,
      alt ? 'ALT_STREET_NAME' : 'STREET_NAME',
      alt ? 'altStreetName' : 'streetName',
      alt ? 'alternateStreetName' : 'primaryStreetName',
    ) || pickContactStr(parts, 'streetName', 'STREET_NAME');
  const buildingName =
    pickContactStr(
      person,
      alt ? 'ALT_BUILDING_NAME' : 'BUILDING_NAME',
      alt ? 'altBuildingName' : 'buildingName',
      alt ? 'alternateBuildingName' : 'primaryBuildingName',
    ) || pickContactStr(parts, 'buildingName', 'BUILDING_NAME');
  const addrLevel =
    pickContactStr(
      person,
      alt ? 'ALT_ADDR_LEVEL' : 'ADDR_LEVEL',
      alt ? 'altAddrLevel' : 'addrLevel',
      alt ? 'alternateAddrLevel' : 'primaryAddrLevel',
    ) || pickContactStr(parts, 'addrLevel', 'ADDR_LEVEL');
  const addrUnit =
    pickContactStr(
      person,
      alt ? 'ALT_ADDR_UNIT' : 'ADDR_UNIT',
      alt ? 'altAddrUnit' : 'addrUnit',
      alt ? 'alternateAddrUnit' : 'primaryAddrUnit',
    ) || pickContactStr(parts, 'addrUnit', 'ADDR_UNIT');
  const postalCode =
    pickContactStr(
      person,
      alt ? 'ALT_POSTAL_CODE' : 'POSTAL_CODE',
      alt ? 'altPostalCode' : 'postalCode',
      alt ? 'alternatePostalCode' : 'primaryPostalCode',
    ) || pickContactStr(parts, 'postalCode', 'POSTAL_CODE');

  const line1 = [blockNo, streetName].filter(Boolean).join(' ').trim();
  const levelUnit = [addrLevel, addrUnit].filter(Boolean).join('-');
  const unit = levelUnit ? `#${levelUnit}` : '';
  const assembled = [line1, unit, buildingName, postalCode ? `SINGAPORE ${postalCode}` : '']
    .filter(Boolean)
    .join(', ')
    .trim();
  return assembled;
}

/** B04 TOTAL_CODE → 표시명 (캐시 없으면 원문 유지) */
export function resolveB04DisplayName(codeOrText: string): string {
  const s = String(codeOrText || '').trim();
  if (!s) return '';
  return nationalityLabelForOutput(s) || s;
}

/**
 * B15(관할) TOTAL_CODE("B15201") → 이름("Singapore").
 *   Contact 의 JURISDICTION 은 TOTAL_CODE 로 저장되는데, 공용 B15 옵션은 value 가 ISO2(BIGO1)라 코드로 못 찾는다.
 */
let b15NameByTotal: Record<string, string> | null = null;
async function b15NameOf(code: string): Promise<string> {
  const c = String(code || '').trim();
  if (!c) return '';
  if (!b15NameByTotal) {
    try {
      const res: any = await baseCodeApi.getCodesByMainCode('CM', 'B15', undefined);
      const codes: any[] = res?.codes || res?.data?.codes || [];
      const map: Record<string, string> = {};
      for (const x of codes) {
        const total = String(x.totalCode || x.TOTAL_CODE || '').trim();
        const name = String(x.codeName || x.CODE_NAME || x.defaultCodeName || x.DEFAULT_CODE_NAME || '').trim();
        const bigo1 = String(x.bigo1 || x.BIGO1 || '').trim().toUpperCase();
        if (total && name) map[total] = name;
        if (bigo1 && name && !map[bigo1]) map[bigo1] = name;
      }
      b15NameByTotal = map;
    } catch {
      return /^B15\d+$/i.test(c) ? '' : c; // 코드 원문은 문서에 넣지 않는다
    }
  }
  const hit = b15NameByTotal[c] || b15NameByTotal[c.toUpperCase()];
  if (hit) return hit;
  return /^B15\d+$/i.test(c) ? '' : c;
}

async function ensureB04Loaded(): Promise<void> {
  if (getB04Options().length) return;
  try {
    await loadCmBaseCodeOptions('B04');
  } catch {
    /* ignore — 원문 코드 유지 */
  }
}

/** Contact 국적 필드 → 문서 표시용 코드명 */
function nationalityDisplayFromPerson(person: Record<string, any>): string {
  const label = pickContactStr(
    person,
    'nationalityName',
    'nationalityText',
    'NATIONALITY_NAME',
    'NATIONALITY_NM',
  );
  // 이미 사람이 읽을 수 있는 이름이면 그대로 (B04 코드 형태만 제외)
  if (label && !/^B\d{5}$/i.test(label)) return label;
  const code = pickContactStr(
    person,
    'NATIONALITY_CD',
    'nationalityCd',
    'nationality',
    'NATIONALITY',
  );
  return resolveB04DisplayName(label || code);
}

/** Country of incorporation 등 B04 공통코드 표시명 */
function countryDisplayFromPerson(person: Record<string, any>): string {
  const label = pickContactStr(
    person,
    'countryName',
    'countryOfIncorporationName',
    'nationalityName',
  );
  if (label && !/^B\d{5}$/i.test(label)) return label;
  const code = pickContactStr(
    person,
    'COUNTRY_CD',
    'countryCd',
    'countryOfIncorporation',
    'country',
    'NATIONALITY_CD',
    'nationality',
  );
  return resolveB04DisplayName(label || code);
}

function formatPersonAddress(
  person: Record<string, any>,
  kind: 'residential' | 'alternate' = 'residential',
): string {
  // 1) SG L_GLOBAL_CONTACT 컬럼
  const fromSg = formatSgGlobalContactAddress(person, kind);
  if (fromSg) return fromSg;

  // 2) 이미 만들어진 한 줄 주소 (bridge / list)
  if (kind === 'alternate') {
    return String(
      person.alternateAddress || person.mailingAddress || person.serviceAddress || '',
    ).trim();
  }
  return String(
    person.residentialAddress ||
      person.primaryAddress ||
      person.address ||
      person.homeAddress ||
      '',
  ).trim();
}

function formatPersonDob(raw: unknown): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  const iso = s.split(/[ T]/)[0];
  // CddVarField 가 ISO 저장 → 세션 dateFormat 표시. YYYY-MM-DD 유지.
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const slash = iso.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
  if (slash) {
    return `${slash[1]}-${slash[2].padStart(2, '0')}-${slash[3].padStart(2, '0')}`;
  }
  return s;
}

/** `*_name` / `*_full_name` / `*_contact_person` → companion stem */
export function personFieldStem(bare: string): string | null {
  if (/_full_name$/.test(bare)) return bare.replace(/_full_name$/, '');
  if (/_contact_person$/.test(bare)) return bare.replace(/_contact_person$/, '');
  if (/_name$/.test(bare)) return bare.replace(/_name$/, '');
  return null;
}

/**
 * Studio % 집계 — 미체크 서비스(srv_a_N)는 의도적 공란이라 미채움으로 세지 않음.
 * yes/no 토글은 'no' 도 채움으로 인정.
 */
export function isProgressFilled(
  key: string,
  val: unknown,
  all: Record<string, string> = {},
): boolean {
  const bare = bareKey(key);
  const s = val == null ? '' : String(val).trim();
  // ACRA Section A — 체크 여부만 의미, 미체크도 완료로 간주
  if (/^srv_a_\d+$/.test(bare)) return true;
  // 서비스 미선택 시 details 필드는 등록되지 않음; 등록됐는데 비면 미채움
  if (/^srv_a_\d+_details$/.test(bare)) return s.length > 0;
  // Corp SH 없을 때 IHC/UPC public 질문은 해당 없음
  if (bare === 'ihc_is_public' || bare === 'upc_is_public') {
    const hasCorp = String(all.has_corporate_shareholder || '').toLowerCase();
    if (hasCorp === 'no' || hasCorp === 'false' || hasCorp === '') return true;
  }
  // T-Sign 전 서명 텍스트는 선택 — 비어 있어도 % 미차감
  if (/_officer_signature$/.test(bare) || bare.endsWith('_signature')) return true;
  // CSP 레터헤드 — 실제 값이 있을 때만 채움으로 인정 (빈 칸이 %에서 숨지 않게)
  if (
    bare === 'csp_address_line2' ||
    bare === 'csp_website' ||
    bare === 'csp_phone' ||
    bare === 'csp_email' ||
    bare === 'csp_address_line1'
  ) {
    return s.length > 0;
  }
  if (!s) return false;
  return true;
}

export interface CddFillCtx {
  values: Record<string, string>;
  activeKey: import('vue').Ref<string>;
  dragActive: import('vue').Ref<boolean>;
  keys: import('vue').Ref<string[]>;
  totalFields: import('vue').ComputedRef<number>;
  filledCount: import('vue').ComputedRef<number>;
  registerKey: (key: string) => void;
  unregisterKey: (key: string) => void;
  labelOf: (key: string) => string;
  typeFor: (key: string) => CddFieldType;
  activate: (key: string) => void;
  openAddress: (key: string) => void;
  setValue: (key: string, val: string) => void;
  exportPayload: () => Record<string, string>;
  /** Panel 이 챗봇 lookup 으로 연결 */
  setActivateHandler: (fn: ((key: string) => void) | null) => void;
  setAddressHandler: (fn: ((key: string) => void) | null) => void;
  /** CDD → Stage 문서 동기화 (applyPerson / 값 변경 후) */
  setAfterChangeHandler: (fn: (() => void) | null) => void;
  applyPersonToActive: (person: Record<string, any>) => void | Promise<void>;
  /** 특정 필드 키에 person 을 적용(활성 필드 아님) — 자동 nominator 등 프로그램적 채움용. */
  applyPersonToKey: (key: string, person: Record<string, any>) => void | Promise<void>;
}

export const CDD_FILL: InjectionKey<CddFillCtx> = Symbol('singbacIncPreCddFill');
export const DOC_SCOPE: InjectionKey<string> = Symbol('singbacIncPreDocScope');

export function provideCddFill(): CddFillCtx {
  const values = reactive<Record<string, string>>({});
  const keys = ref<string[]>([]);
  const activeKey = ref('');
  const dragActive = ref(false);
  let activateHandler: ((key: string) => void) | null = null;
  let addressHandler: ((key: string) => void) | null = null;
  let afterChangeHandler: (() => void) | null = null;

  const totalFields = computed(() => keys.value.length);
  const filledCount = computed(() =>
    keys.value.filter((k) => isProgressFilled(k, values[k], values)).length,
  );

  const keyRefs = new Map<string, number>();
  /** 회사명 별칭 전파 중 재진입 방지 */
  let syncingCompanyName = false;
  /** UEN / Registration No. 별칭 전파 중 재진입 방지 */
  let syncingRegNo = false;
  /** 등록주소 별칭 전파 중 재진입 방지 */
  let syncingAddress = false;

  const COMPANY_NAME_ALIASES = ['company_name', 'entity_name', 'proposed_company_name'] as const;
  /** CDD entity_uen ↔ Form45/Constitution company_registration_no */
  const REG_NO_ALIASES = ['entity_uen', 'company_registration_no', 'entity_registration_no'] as const;
  /** First Res registered_office_address ↔ Share App / CSP docs company_registered_address */
  const ADDRESS_ALIASES = ['company_registered_address', 'registered_office_address'] as const;

  function isCompanyNameAliasKey(key: string): boolean {
    const bare = bareKey(key);
    if (COMPANY_NAME_ALIASES.includes(bare as (typeof COMPANY_NAME_ALIASES)[number])) {
      // 루트 entity/company/proposed 만 (스코프 company_name 은 아래)
      if (!key.includes('/')) return true;
    }
    // Form45 / 45B / Notice 등 DocScope 의 company_name (cs_company_name·agent 제외)
    return key.includes('/') && bare === 'company_name';
  }

  function isRegNoAliasKey(key: string): boolean {
    const bare = bareKey(key);
    if (REG_NO_ALIASES.includes(bare as (typeof REG_NO_ALIASES)[number])) {
      if (!key.includes('/')) return true;
    }
    return key.includes('/') && bare === 'company_registration_no';
  }

  function isAddressAliasKey(key: string): boolean {
    const bare = bareKey(key);
    if (ADDRESS_ALIASES.includes(bare as (typeof ADDRESS_ALIASES)[number])) {
      if (!key.includes('/')) return true;
    }
    return (
      key.includes('/') &&
      (bare === 'company_registered_address' || bare === 'registered_office_address')
    );
  }

  function sharedCompanyNameNow(): string {
    const candidates = COMPANY_NAME_ALIASES.map((a) => String(values[a] || '').trim()).filter(Boolean);
    for (const k of [...Object.keys(values), ...keys.value]) {
      if (!k.includes('/') || bareKey(k) !== 'company_name') continue;
      const v = String(values[k] || '').trim();
      if (v) candidates.push(v);
    }
    if (!candidates.length) return '';
    return candidates.reduce((a, b) => (b.length > a.length ? b : a));
  }

  function sharedRegNoNow(): string {
    const candidates = REG_NO_ALIASES.map((a) => String(values[a] || '').trim()).filter(Boolean);
    for (const k of [...Object.keys(values), ...keys.value]) {
      if (!k.includes('/') || bareKey(k) !== 'company_registration_no') continue;
      const v = String(values[k] || '').trim();
      if (v) candidates.push(v);
    }
    if (!candidates.length) return '';
    return candidates.reduce((a, b) => (b.length > a.length ? b : a));
  }

  function sharedAddressNow(): string {
    const candidates = ADDRESS_ALIASES.map((a) => String(values[a] || '').trim()).filter(Boolean);
    for (const k of [...Object.keys(values), ...keys.value]) {
      if (!k.includes('/')) continue;
      const bare = bareKey(k);
      if (bare !== 'company_registered_address' && bare !== 'registered_office_address') continue;
      const v = String(values[k] || '').trim();
      if (v) candidates.push(v);
    }
    if (!candidates.length) return '';
    return candidates.reduce((a, b) => (b.length > a.length ? b : a));
  }

  function propagateCompanyName(val: string) {
    if (syncingCompanyName) return;
    syncingCompanyName = true;
    try {
      for (const alias of COMPANY_NAME_ALIASES) {
        if (values[alias] !== val) values[alias] = val;
      }
      const seen = new Set<string>([...COMPANY_NAME_ALIASES]);
      for (const k of [...Object.keys(values), ...keys.value]) {
        if (seen.has(k)) continue;
        if (!k.includes('/') || bareKey(k) !== 'company_name') continue;
        seen.add(k);
        if (values[k] !== val) values[k] = val;
      }
      // 아직 register 안 된 기본 스코프에도 미리 기록 (Skip Entity 수동입력 → Notice 빈칸 방지)
      for (const sc of ['ctrl_1/']) {
        const k = sc + 'company_name';
        if (seen.has(k)) continue;
        if (values[k] !== val) values[k] = val;
      }
    } finally {
      syncingCompanyName = false;
    }
  }

  function propagateRegNo(val: string) {
    if (syncingRegNo) return;
    syncingRegNo = true;
    try {
      for (const alias of REG_NO_ALIASES) {
        if (values[alias] !== val) values[alias] = val;
      }
      const seen = new Set<string>([...REG_NO_ALIASES]);
      for (const k of [...Object.keys(values), ...keys.value]) {
        if (seen.has(k)) continue;
        if (!k.includes('/') || bareKey(k) !== 'company_registration_no') continue;
        seen.add(k);
        if (values[k] !== val) values[k] = val;
      }
      for (const sc of ['ctrl_1/']) {
        const k = sc + 'company_registration_no';
        if (seen.has(k)) continue;
        if (values[k] !== val) values[k] = val;
      }
    } finally {
      syncingRegNo = false;
    }
  }

  function propagateAddress(val: string) {
    if (syncingAddress) return;
    syncingAddress = true;
    try {
      for (const alias of ADDRESS_ALIASES) {
        if (values[alias] !== val) values[alias] = val;
      }
      const seen = new Set<string>([...ADDRESS_ALIASES]);
      for (const k of [...Object.keys(values), ...keys.value]) {
        if (seen.has(k)) continue;
        if (!k.includes('/')) continue;
        const bare = bareKey(k);
        if (bare !== 'company_registered_address' && bare !== 'registered_office_address') continue;
        seen.add(k);
        if (values[k] !== val) values[k] = val;
      }
    } finally {
      syncingAddress = false;
    }
  }

  function registerKey(key: string) {
    keyRefs.set(key, (keyRefs.get(key) || 0) + 1);
    if (!keys.value.includes(key)) keys.value.push(key);
    // Form45 등이 늦게 mount 돼도 CDD에서 이미 친 값을 시드
    if (key.includes('/') && bareKey(key) === 'company_name') {
      const shared = sharedCompanyNameNow();
      if (shared && !String(values[key] || '').trim()) {
        values[key] = shared;
      }
    }
    if (
      (!key.includes('/') && REG_NO_ALIASES.includes(bareKey(key) as (typeof REG_NO_ALIASES)[number])) ||
      (key.includes('/') && bareKey(key) === 'company_registration_no')
    ) {
      const shared = sharedRegNoNow();
      if (shared && !String(values[key] || '').trim()) {
        values[key] = shared;
      }
    }
    if (
      (!key.includes('/') && ADDRESS_ALIASES.includes(bareKey(key) as (typeof ADDRESS_ALIASES)[number])) ||
      (key.includes('/') &&
        (bareKey(key) === 'company_registered_address' || bareKey(key) === 'registered_office_address'))
    ) {
      const shared = sharedAddressNow();
      if (shared && !String(values[key] || '').trim()) {
        values[key] = shared;
      }
    }
  }
  function unregisterKey(key: string) {
    const n = (keyRefs.get(key) || 0) - 1;
    if (n > 0) {
      keyRefs.set(key, n);
      return;
    }
    keyRefs.delete(key);
    const i = keys.value.indexOf(key);
    if (i >= 0) keys.value.splice(i, 1);
    if (activeKey.value === key) activeKey.value = '';
  }
  function setValue(key: string, val: string) {
    if (values[key] === val) {
      // 값이 같아도 별칭이 어긋난 경우(짤림 복구) 전파
      if (isCompanyNameAliasKey(key) && !syncingCompanyName) {
        const mismatched =
          COMPANY_NAME_ALIASES.some((a) => (values[a] || '') !== val) ||
          [...Object.keys(values), ...keys.value].some(
            (k) => k.includes('/') && bareKey(k) === 'company_name' && (values[k] || '') !== val,
          );
        if (mismatched) {
          propagateCompanyName(val);
          notifyChanged();
        }
      }
      if (isRegNoAliasKey(key) && !syncingRegNo) {
        const mismatched =
          REG_NO_ALIASES.some((a) => (values[a] || '') !== val) ||
          [...Object.keys(values), ...keys.value].some(
            (k) =>
              k.includes('/') &&
              bareKey(k) === 'company_registration_no' &&
              (values[k] || '') !== val,
          );
        if (mismatched) {
          propagateRegNo(val);
          notifyChanged();
        }
      }
      if (isAddressAliasKey(key) && !syncingAddress) {
        const mismatched =
          ADDRESS_ALIASES.some((a) => (values[a] || '') !== val) ||
          [...Object.keys(values), ...keys.value].some((k) => {
            if (!k.includes('/')) return false;
            const bare = bareKey(k);
            if (bare !== 'company_registered_address' && bare !== 'registered_office_address') {
              return false;
            }
            return (values[k] || '') !== val;
          });
        if (mismatched) {
          propagateAddress(val);
          notifyChanged();
        }
      }
      return;
    }
    values[key] = val;
    if (isCompanyNameAliasKey(key)) {
      propagateCompanyName(val);
      notifyChanged();
    }
    if (isRegNoAliasKey(key)) {
      propagateRegNo(val);
      notifyChanged();
    }
    if (isAddressAliasKey(key)) {
      propagateAddress(val);
      notifyChanged();
    }
  }

  function notifyChanged() {
    afterChangeHandler?.();
  }

  function activate(key: string) {
    activeKey.value = key;
    if (activateHandler) activateHandler(key);
  }

  function openAddress(key: string) {
    activeKey.value = key;
    if (addressHandler) addressHandler(key);
  }

  function applyPersonToActive(person: Record<string, any>) {
    return applyPersonToKey(activeKey.value, person);
  }

  async function applyPersonToKey(k: string, person: Record<string, any>) {
    if (!k || !person) return;
    const name =
      person.name ||
      person.contactName ||
      [person.firstName, person.middleName, person.surname].filter(Boolean).join(' ') ||
      '';
    if (!name) return;
    const bare = bareKey(k);
    const sc = scopeOf(k);
    setValue(k, name);

    await ensureB04Loaded();

    const idNo = String(
      person.nricNo ||
        person.passportNo ||
        person.contactRegNo ||
        person.identificationNo ||
        person.regNo ||
        '',
    ).trim();
    const nationality = nationalityDisplayFromPerson(person);
    const country = countryDisplayFromPerson(person);
    const addr = formatPersonAddress(person, 'residential');
    const altAddr = formatPersonAddress(person, 'alternate');
    const email = String(person.email || person.contactEmail || '').trim();
    const dob = formatPersonDob(person.dateOfBirth || person.dob);
    const mobile = [person.areaCode, person.mobileNo]
      .map((x) => String(x || '').trim())
      .filter(Boolean)
      .join(' ')
      .trim() || String(person.mobileNo || person.phone || '').trim();
    const regNo = String(person.contactRegNo || person.registrationNo || person.regNo || idNo).trim();

    // Entity / 회사명 필드 — 등록번호·주소
    if (typeOf(k) === 'entity') {
      if (/controller_corporate_name$/.test(bare)) {
        const pfx = bare.replace(/_controller_corporate_name$/, '');
        if (regNo) setValue(sc + `${pfx}_controller_uen`, regNo);
        if (addr) setValue(sc + `${pfx}_controller_registered_office`, addr);
        if (email) setValue(sc + `${pfx}_controller_email`, email);
        if (mobile) setValue(sc + `${pfx}_controller_contact_no`, mobile);
        notifyChanged();
        return;
      }
      if (bare === 'cs_company_name') {
        if (regNo) setValue(sc + 'cs_registration_no', regNo);
        if (addr) setValue(sc + 'cs_registered_address', addr);
        if (country) setValue(sc + 'cs_country_of_incorporation', country);
        notifyChanged();
        return;
      }
      if (bare === 'corporate_shareholder_name') {
        if (regNo) setValue(sc + 'corporate_shareholder_registration_no', regNo);
        if (addr) setValue(sc + 'corporate_shareholder_address', addr);
        if (country) setValue(sc + 'corporate_shareholder_country', country);
        notifyChanged();
        return;
      }
      if (bare === 'company_name' || bare === 'entity_name' || bare === 'proposed_company_name') {
        if (regNo) {
          setValue(sc + 'company_registration_no', regNo);
          setValue(sc + 'entity_registration_no', regNo);
        }
        if (addr) {
          setValue(sc + 'entity_registered_address', addr);
          setValue(sc + 'registered_address', addr);
        }
        notifyChanged();
        return;
      }
      // csp_name / ihc / upc / agent_company — 이름 + 연락처 + UEN
      if (bare === 'csp_name') {
        if (regNo) setValue('csp_uen', regNo);
        if (addr) {
          setValue('csp_address_line1', addr);
          setValue('csp_full_address', addr);
        }
        if (email) setValue('csp_email', email);
        if (mobile) setValue('csp_phone', mobile);
      }
      if (bare === 'ihc_name') {
        if (regNo) setValue(sc + 'ihc_registration_no', regNo);
        if (country) setValue(sc + 'ihc_country', country);
      }
      if (bare === 'upc_name') {
        if (regNo) setValue(sc + 'upc_registration_no', regNo);
        if (country) setValue(sc + 'upc_country', country);
      }
      notifyChanged();
      return;
    }

    // Notice to Controller (b) 법인 컨트롤러 — "Name of the Company" 에서 법인 Contact 를 고르면
    //   UEN·등록사무소·법적형태·관할/근거법·법인등록부(이름·번호)를 Contact 상세(L_GLOBAL_CONTACT)에서 채운다.
    //   (예전엔 person stem 규칙(controller_entity_*)으로 빠져 아무 칸도 안 채워졌다.)
    if (bare === 'controller_entity_name') {
      const pick = (...ks: string[]) => {
        for (const kk of ks) {
          const v = String(person[kk] ?? '').trim();
          if (v) return v;
        }
        return '';
      };
      const uen = pick('CONTACT_REG_NO', 'contactRegNo', 'registrationNo', 'regNo', 'uen');
      const jurCode = pick('JURISDICTION', 'jurisdiction', 'jurisdictionCd');
      const jurName = pick('jurisdictionName') || (await b15NameOf(jurCode));
      const statute = pick('INCORPORATION_STATUTE', 'incorporationStatute');
      // 다른 법인으로 다시 고르면 이전 법인 값이 남지 않게 (b) 칸을 먼저 비운다.
      for (const key of [
        'controller_entity_uen', 'controller_registered_office_address', 'controller_legal_form',
        'controller_jurisdiction', 'controller_corporate_register_name', 'controller_corporate_register_no',
      ]) {
        if (String(values[sc + key] || '').trim()) setValue(sc + key, '');
      }
      const setIf = (key: string, val: string) => { if (val) setValue(sc + key, val); };
      // UEN 은 ACRA 가 발급한 번호 — 관할이 싱가포르가 아닌 법인이면 Contact 의 등록번호는 본국 번호라 UEN 칸에 넣지 않는다.
      //   관할을 모르면(코드 해석 실패 포함) 싱가포르로 본다.
      const foreign = !!jurName && !/singapore/i.test(jurName);
      if (!foreign) setIf('controller_entity_uen', uen);
      setIf('controller_registered_office_address', addr);
      setIf('controller_legal_form', pick('LEGAL_FORM', 'legalForm'));
      setIf('controller_jurisdiction', [jurName, statute].filter(Boolean).join(', '));
      setIf('controller_corporate_register_name', pick('CORPORATE_ENTITY_REGISTER', 'corporateEntityRegister'));
      setIf('controller_corporate_register_no', uen);
      notifyChanged();
      return;
    }

    // Contact Person 특수 키 (stem 규칙과 companion 키가 다름)
    if (bare === 'entity_contact_person') {
      if (mobile) setValue(sc + 'entity_mobile_number', mobile);
      if (email) setValue(sc + 'entity_email', email);
      notifyChanged();
      return;
    }
    if (bare === 'cs_contact_person') {
      if (mobile) {
        setValue(sc + 'cs_contact_no_office', mobile);
        setValue(sc + 'cs_mobile_no', mobile);
      }
      if (email) setValue(sc + 'cs_email', email);
      notifyChanged();
      return;
    }

    const stem = personFieldStem(bare);
    if (!stem) return;

    const put = (suffix: string, val: string) => {
      if (val) setValue(sc + `${stem}${suffix}`, val);
    };

    const aliases = String(
      person.aliases ?? person.alias ?? person.otherName ?? person.OTHER_NAME ?? person.aliasName ?? '',
    ).trim();
    put('_identification_no', idNo);
    put('_id_no', idNo);
    put('_nric', idNo);
    put('_aliases', aliases);
    put('_nationality', nationality);
    put('_address', addr);
    put('_residential_address', addr);
    put('_alternate_address', altAddr);
    put('_email', email);
    put('_date_of_birth', dob);
    put('_dob', dob);
    put('_mobile_no', mobile);
    put('_tel_no', mobile);
    put('_contact_no', mobile);

    // Notice to Controller (a) — Dear(controller_name) 와 Name in full(controller_full_name)은 같은 사람.
    //   한쪽에서 고르면 비어 있는 다른 쪽도 채운다. 신분증은 여권이면 Passport No, NRIC/FIN 이면 NRIC/FIN No. 로.
    if (stem === 'controller' && (bare === 'controller_name' || bare === 'controller_full_name')) {
      const other = bare === 'controller_name' ? 'controller_full_name' : 'controller_name';
      if (!String(values[sc + other] || '').trim()) setValue(sc + other, name);
      if (idNo) {
        const idTypeText = String(
          person.idTypeName || person.ID_TYPE || person.idType || person.idTypeCd || person.ID_TYPE_CD || '',
        ).toLowerCase();
        const sgNricFin = /^[STFGM]\d{7}[A-Z]$/i.test(idNo);
        const isPassport = /passport|\bpp\b/.test(idTypeText) || (!/nric|fin/.test(idTypeText) && !sgNricFin);
        if (isPassport) {
          setValue(sc + 'controller_passport_no', idNo);
          if (values[sc + 'controller_identification_no'] === idNo) setValue(sc + 'controller_identification_no', '');
        }
      }
    }

    // Form 45 interpreter — Word 키는 interpreter_nric (interpreter_identification_no 아님)
    if (bare === 'interpreter_name' && idNo) {
      setValue(sc + 'interpreter_nric', idNo);
    }

    // Form 45 / 45B 명시 키 (stem 과 동일하나 중복 set 무해)
    if (bare === 'director_name' || bare === 'secretary_name') {
      if (idNo) setValue(sc + `${stem}_identification_no`, idNo);
      if (nationality) setValue(sc + `${stem}_nationality`, nationality);
      if (addr) setValue(sc + `${stem}_address`, addr);
      if (altAddr && bare === 'director_name') {
        setValue(sc + 'director_alternate_address', altAddr);
      }
      if (bare === 'director_name') {
        const idTypeCd =
          person.idTypeCd || person.ID_TYPE_CD || person.contactIdTypeCd || person.idType;
        const idTypeLabel = person.idTypeName || person.ID_TYPE_NAME || person.idTypeLabel;
        const usesPassport = resolveForm45UsesPassport({ directorIdTypeCd: idTypeCd, idTypeLabel });
        setValue(sc + 'uses_passport', usesPassport ? 'Yes' : 'No');
      }
    }

    // CAF director → Form 45 alternate / nationality 도 바로 반영
    const dirM = bare.match(/^dir(\d+)_director_name$/);
    if (dirM) {
      const fsc = `f45_${dirM[1]}/`;
      if (nationality) setValue(fsc + 'director_nationality', nationality);
      if (addr) setValue(fsc + 'director_address', addr);
      if (altAddr) setValue(fsc + 'director_alternate_address', altAddr);
      if (idNo) setValue(fsc + 'director_identification_no', idNo);
      setValue(fsc + 'director_name', name);
      const idTypeCd =
        person.idTypeCd || person.ID_TYPE_CD || person.contactIdTypeCd || person.idType;
      const idTypeLabel = person.idTypeName || person.ID_TYPE_NAME || person.idTypeLabel;
      const usesPassport = resolveForm45UsesPassport({ directorIdTypeCd: idTypeCd, idTypeLabel });
      setValue(fsc + 'uses_passport', usesPassport ? 'Yes' : 'No');
    }

    notifyChanged();
  }

  function exportPayload() {
    const out: Record<string, string> = {};
    keys.value.forEach((k) => {
      out[k] = values[k] || '';
    });
    // 등록되지 않았지만 setValue 된 키도 포함 (체크박스 등)
    for (const [k, v] of Object.entries(values)) {
      if (v && out[k] == null) out[k] = v;
    }
    return out;
  }

  const ctx: CddFillCtx = {
    values,
    activeKey,
    dragActive,
    keys,
    totalFields,
    filledCount,
    registerKey,
    unregisterKey,
    labelOf: (k) => labelize(k),
    typeFor: (k) => typeOf(k),
    activate,
    openAddress,
    setValue,
    exportPayload,
    setActivateHandler: (fn) => {
      activateHandler = fn;
    },
    setAddressHandler: (fn) => {
      addressHandler = fn;
    },
    setAfterChangeHandler: (fn) => {
      afterChangeHandler = fn;
    },
    applyPersonToActive,
    applyPersonToKey,
  };
  provide(CDD_FILL, ctx);
  return ctx;
}

export function useCddFill(): CddFillCtx {
  const ctx = inject(CDD_FILL);
  if (!ctx) throw new Error('useCddFill() requires provideCddFill() in an ancestor');
  return ctx;
}
